import AbstractCustomerValueModel from '../abstract_customer_value_model';
import SymplrBenefitsChart from '../../charts/symplr/benefits_chart';
import BenefitsChartValue from '../../charts/benefits_chart_value';
import BenefitsWaterfallChart from '../../charts/symplr/benefits_waterfall_chart';

class SymplrWfm extends AbstractCustomerValueModel {
  custom_chart_classes() {
    return { SymplrBenefitsChart, BenefitsChartValue, BenefitsWaterfallChart };
  }

  // Impact Variables
  percent_reduction_in_labor_expenses() {
    return this.input.percent_reduction_in_labor_expenses / 100;
  }

  percent_reduction_in_labor_expenses_attr() {
    return this.input.percent_reduction_in_labor_expenses_attr / 100;
  }

  reduction_in_payroll_penalties() {
    return this.input.reduction_in_payroll_penalties;
  }

  percent_increase_in_payroll_team_capacity() {
    return this.input.percent_increase_in_payroll_team_capacity / 100;
  }

  percent_reduction_in_time_on_schedules() {
    return this.input.percent_reduction_in_time_on_schedules / 100;
  }

  percent_reduction_in_time_communicating() {
    return this.input.percent_reduction_in_time_communicating / 100;
  }

  percent_reduction_in_outdated_communication_spend() {
    return this.input.percent_reduction_in_outdated_communication_spend / 100;
  }

  percent_reduction_in_length_of_stay() {
    return this.input.percent_reduction_in_length_of_stay / 100;
  }

  percent_reduction_in_length_of_stay_attr() {
    return this.input.percent_reduction_in_length_of_stay_attr / 100;
  }

  percent_improvement_in_retention() {
    return this.input.percent_improvement_in_retention / 100;
  }

  percent_improvement_in_retention_attr() {
    return this.input.percent_improvement_in_retention_attr / 100;
  }

  percent_improvement_in_pt_outcomes() {
    return this.input.percent_improvement_in_pt_outcomes / 100;
  }

  percent_improvement_in_pt_outcomes_attr() {
    return this.input.percent_improvement_in_pt_outcomes_attr / 100;
  }

  annual_labor_expense() { //G15
    return this.input.annual_labor_expense;
  }

  annual_nurse_salary() { //G39
    return this.input.annual_nurse_salary;
  }

  annual_payroll_mgr_salary() { //G36
    return this.input.annual_payroll_mgr_salary;
  }

  annual_physician_salary() { //G38
    return this.input.annual_physician_salary;
  }

  annual_staffing_mgr_salary() { //G37
    return this.input.annual_staffing_mgr_salary;
  }

  avg_cost_per_day() { //G27
    return this.input.avg_cost_per_day;
  }

  avg_length_of_stay() { //G10
    return this.input.avg_length_of_stay;
  }

  avg_time_on_staffing_and_scheduling() { //G18
    return this.input.avg_time_on_staffing_and_scheduling;
  }

  avg_variable_cost_per_inpatient_stay() { //Benefit Calculations - H125
    return 20 / 100;
  }

  burden_rate() {
    return this.input.burden_rate;
  }

  employee_replacement_cost() { //G32
    return 1;
  }

  fte_managing_staffing() { //G19
    return this.input.fte_managing_staffing;
  }

  fte_on_payroll_team() { //G17
    return this.input.fte_on_payroll_team;
  }

  monthly_cost_per_pager() { //G24
    return this.input.monthly_cost_per_pager;
  }

  net_operating_profit_margin() { //G11
    return 18.13 / 100;
  }

  net_pt_revenue() { //G8
    return this.input.net_pt_revenue;
  }

  number_of_beds() { //Configurator - G22
    return this.input.number_of_beds;
  }

  number_of_employees() { //G9
    return this.input.number_of_employees;
  }

  nurses_on_staff() { //G22
    return this.input.nurses_on_staff;
  }

  pagers_in_use() { //G23
    return this.input.pagers_in_use;
  }

  patient_discharges_per_year() { //G28
    return this.input.patient_discharges_per_year;
  }

  payroll_noncompliance_penalty() { //G16
    return this.input.payroll_noncompliance_penalty;
  }

  physicians_on_staff() { //G21
    return this.input.physicians_on_staff;
  }

  product_ss() {
    return this.input.product_ss;
  }

  product_ta() {
    return this.input.product_ta;
  }

  product_cc() {
    return this.input.product_cc;
  }

  retention_rate() { //G31
    return this.input.retention_rate / 100;
  }

  session_type() {
    return this.input.session_type;
  }

  time_communicating_about_care() { //G20
    return this.input.time_communicating_about_care;
  }

  // Derived Inputs
  fully_burdened_salary(salary) {
    return salary * this.burden_rate();
  }

  weeks_per_year() { //Configurator - G45
    return 50;
  }

  hours_per_year() { //Configurator - G47
    return 1880;
  }

  // Benefit 1
  reduce_labor_expenses() {
    if ((this.product_ss() + this.product_ta()) === 0) { return 0; }

    return this.annual_labor_expense()
      * this.percent_reduction_in_labor_expenses() * this.percent_reduction_in_labor_expenses_attr();
  }

  // Benefit 2
  avoid_payroll_penalty() {
    if (this.product_ta() === 0) { return 0; }
    if (this.session_type() === 'self_service') { return 0; }

    return this.payroll_noncompliance_penalty() * this.reduction_in_payroll_penalties();
  }

  // Benefit 3
  increase_payroll_team_capacity() {
    if ((this.product_ss() + this.product_ta()) === 0) { return 0; }

    return this.fte_on_payroll_team() * this.fully_burdened_salary(this.annual_payroll_mgr_salary())
      * this.percent_increase_in_payroll_team_capacity();
  }

  // Benefit 4
  reduce_time_on_schedules() {
    if ((this.product_ss() + this.product_ta()) === 0) { return 0; }

    return (this.avg_time_on_staffing_and_scheduling() * this.fte_managing_staffing() * this.weeks_per_year())
      * (this.fully_burdened_salary(this.annual_staffing_mgr_salary()) / this.hours_per_year())
    * this.percent_reduction_in_time_on_schedules();
  }

  // Benefit 5
  reduce_time_communicating() {
    if (this.product_cc() === 0) { return 0; }

    const physician_cost = (this.fully_burdened_salary(this.annual_physician_salary()) / this.hours_per_year())
      * this.weeks_per_year() * this.physicians_on_staff() * this.time_communicating_about_care();

    const nurse_cost = (this.fully_burdened_salary(this.annual_nurse_salary()) / this.hours_per_year())
      * this.weeks_per_year() * this.nurses_on_staff() * this.time_communicating_about_care();

    return (physician_cost + nurse_cost) * this.percent_reduction_in_time_communicating();
  }

  // Benefit 6
  reduce_communication_system_spend() {
    if (this.product_cc() === 0) { return 0; }

    return this.pagers_in_use() * this.monthly_cost_per_pager() * 12
      * this.percent_reduction_in_outdated_communication_spend();
  }

  // Benefit 7
  reduce_length_of_stay() {
    if (this.product_cc() === 0) { return 0; }

    return this.avg_length_of_stay() * this.avg_cost_per_day() * this.avg_variable_cost_per_inpatient_stay()
      * this.patient_discharges_per_year()
      * this.percent_reduction_in_length_of_stay() * this.percent_reduction_in_length_of_stay_attr();
  }

  // Benefit 8
  improve_retention() {
    const before = (1 - this.retention_rate())
      * this.number_of_employees()
      * this.employee_replacement_cost()
      * this.fully_burdened_salary(this.annual_nurse_salary());

    const new_retention_rate = this.retention_rate() * (1 + this.percent_improvement_in_retention());

    const after = (1 - new_retention_rate)
      * this.number_of_employees()
      * this.employee_replacement_cost()
      * this.fully_burdened_salary(this.annual_nurse_salary());

    return (before - after) * this.percent_improvement_in_retention_attr();
  }

  // Benefit 9
  improve_pt_outcomes() {
    if (this.product_cc() === 0) { return 0; }

    return this.net_pt_revenue()
      * this.percent_improvement_in_pt_outcomes()
      * this.percent_improvement_in_pt_outcomes_attr();
  }

  all_benefits() {
    return [
      'reduce_labor_expenses',
      'avoid_payroll_penalty',
      'increase_payroll_team_capacity',
      'reduce_time_on_schedules',
      'reduce_time_communicating',
      'reduce_communication_system_spend',
      'reduce_length_of_stay',
      'improve_retention',
      'improve_pt_outcomes'
    ];
  }

  value_drivers() {
    return [
      'streamline_healthcare_operations',
      'grow_protect_revenue',
      'mitigate_risk'
    ];
  }

  streamline_healthcare_operations() {
    return this.reduce_labor_expenses()
      + this.avoid_payroll_penalty()
      + this.increase_payroll_team_capacity()
      + this.reduce_time_on_schedules()
      + this.reduce_time_communicating()
      + this.reduce_communication_system_spend();
  }

  grow_protect_revenue() {
    return this.reduce_length_of_stay();
  }

  mitigate_risk() {
    return this.improve_retention()
      + this.improve_pt_outcomes();
  }

  adoption_rate_year_1() {
    return this.input.adoption_rate_year_1 / 100;
  }

  adoption_rate_year_2() {
    return this.input.adoption_rate_year_2 / 100;
  }

  adoption_rate_year_3() {
    return this.input.adoption_rate_year_3 / 100;
  }

  adoption_rate_year_4() {
    return this.input.adoption_rate_year_4 / 100;
  }

  adoption_rate_year_5() {
    return this.input.adoption_rate_year_5 / 100;
  }

  adoption_rate(year) {
    switch (year) {
      case 1: return this.adoption_rate_year_1();
      case 2: return this.adoption_rate_year_2();
      case 3: return this.adoption_rate_year_3();
      case 4: return this.adoption_rate_year_4();
      case 5: return this.adoption_rate_year_5();
      default: return 1.0;
    }
  }

  cost_per_additional_employee() {
    return 4.5;
  }

  wfm_base_fee() {
    return 97500;
  }

  wfm_license_fee() {
    if ((this.product_ss() + this.product_ta()) === 0) { return 0; }

    return this.input.wfm_license_fee;
  }

  wfm_implementation_fee() {
    if ((this.product_ss() + this.product_ta()) === 0) { return 0; }

    return this.input.wfm_implementation_fee;
  }

  cc_price_per_bed() {
    return 736.36;
  }

  cc_license_fee() {
    if (this.product_cc() !== 1) { return 0; }

    return this.input.cc_license_fee;
  }

  cc_implementation_fee() {
    if (this.product_cc() !== 1) { return 0; }

    return this.input.cc_implementation_fee;
  }

  annual_price_increase(year) {
    return (1 + (this.input.annual_increase / 100)) ** (year - 1);
  }

  total_annual_investment(year = 1) {
    const annual_cost = this.wfm_license_fee() + this.cc_license_fee();

    if (year !== 1) {
      return annual_cost * this.annual_price_increase(year);
    }

    return annual_cost + this.cc_implementation_fee() + this.wfm_implementation_fee();
  }

  streamline_healthcare_operations_label() {
    return this.input.streamline_healthcare_operations_label;
  }

  grow_protect_revenue_label() {
    return this.input.grow_protect_revenue_label;
  }

  mitigate_risk_label() {
    return this.input.mitigate_risk_label;
  }

  reduce_labor_expenses_label() {
    return this.input.reduce_labor_expenses_label;
  }

  avoid_payroll_penalty_label() {
    return this.input.avoid_payroll_penalty_label;
  }

  increase_payroll_team_capacity_label() {
    return this.input.increase_payroll_team_capacity_label;
  }

  reduce_time_on_schedules_label() {
    return this.input.reduce_time_on_schedules_label;
  }

  reduce_time_communicating_label() {
    return this.input.reduce_time_communicating_label;
  }

  reduce_communication_system_spend_label() {
    return this.input.reduce_communication_system_spend_label;
  }

  reduce_length_of_stay_label() {
    return this.input.reduce_length_of_stay_label;
  }

  improve_retention_label() {
    return this.input.improve_retention_label;
  }

  improve_pt_outcomes_label() {
    return this.input.improve_pt_outcomes_label;
  }
}

export default SymplrWfm;
